import { cn } from "#app/utils/misc.tsx";
import React from "react";
import { motion } from "motion/react";


type StepperContextValue = {
    currentStep: number;
    totalSteps: number;
    orientation: 'horizontal' | 'vertical'; // Add orientation
};

const StepperContext = React.createContext<StepperContextValue | null>(null);

const useFormStepper = () => {
    const context = React.useContext(StepperContext);
    if (!context) {
        throw new Error("useFormStepper must be used within a FormStepperProvider");
    }
    return context;
}

interface StepperProps extends React.ComponentPropsWithoutRef<'ol'> {
    /** The currently active step (zero-based index). */
    currentStep: number;
    /** The orientation of the stepper. Defaults to 'horizontal'. */
    orientation?: 'horizontal' | 'vertical';
}

const FormStepper = React.forwardRef<HTMLOListElement, StepperProps>(({ currentStep, children, className, orientation = 'horizontal', ...props }, ref) => {
    const childrenArray = React.Children.toArray(children)
    const totalSteps = childrenArray.length;
    if (currentStep < 0 || currentStep >= totalSteps) {
        console.warn(
          `Stepper: currentStep index (${currentStep}) is out of bounds (0-${totalSteps - 1}).`
        );
    }

    const contextValue: StepperContextValue = React.useMemo(() => ({
        currentStep: Math.max(0, Math.min(currentStep, totalSteps - 1)), // Clamp currentStep to valid range
        totalSteps,
        orientation,
    }), [currentStep, totalSteps, orientation]);

    return (
        <StepperContext.Provider value={contextValue}>
            <ol
                ref={ref}
                className={cn(
                    orientation === 'horizontal' ? 'flex items-start w-full text-xs sm:text-base' : 'flex flex-col items-start space-y-4',
                    className
                )}
                {...props}
            >
                {React.Children.map(childrenArray, (child, index) => {
                    if (!React.isValidElement(child)) {
                    return child;
                    }
                    // Inject index and isLast props into StepperItem
                    return React.cloneElement(child as React.ReactElement<any>, {
                    index,
                    isLast: index === totalSteps - 1,
                    });
                })}
            </ol>
        </StepperContext.Provider>
    )
})

interface StepperItemProps extends React.ComponentPropsWithoutRef<'li'> {
    label: React.ReactNode;
    description?: React.ReactNode;
    // These are injected by Stepper
    index?: number;
    isLast?: boolean;
}

const StepperItem = React.forwardRef<HTMLLIElement, StepperItemProps>(
    ({ className, label, description, index, isLast, ...props }, ref) => {
      const { currentStep, orientation } = useFormStepper();
  
      // Ensure index is defined (it should be injected by Stepper)
      if (index === undefined) {
          console.error("StepperItem must be a direct child of Stepper.");
          return null;
      }
  
      const isCompleted = index < currentStep;
      const isCurrent = index === currentStep;
      const isActive = index <= currentStep; // Useful for label color, etc.

      // Animation Variants
      const transition = { duration: 0.3, ease: "easeInOut" as const };

      const circleVariants = {
        inactive: {
            backgroundColor: "hsl(var(--muted))",
            borderColor: "hsl(var(--muted))",
            color: "hsl(var(--muted-foreground))",
            scale: 1,
        },
        current: {
            backgroundColor: "hsl(var(--muted))",
            borderColor: "hsl(var(--primary))", // Highlight border
            color: "hsl(var(--muted-foreground))",
            scale: 1.1, // Pop effect
        },
        completed: {
            backgroundColor: "hsl(var(--primary))",
            borderColor: "hsl(var(--primary))",
            color: "hsl(var(--primary-foreground))",
            scale: 1,
        },
      };

      const lineVariants = {
        hidden: {
            scaleX: orientation === 'horizontal' ? 0 : 1,
            scaleY: orientation === 'vertical' ? 0 : 1,
        },
        visible: {
            scaleX: 1,
            scaleY: 1,
        },
      };

      // Determine current animation state
      const circleState = isCompleted ? 'completed' : isCurrent ? 'current' : 'inactive';
      const lineState = isCompleted ? 'visible' : 'hidden';
  
      return (
        <li
          ref={ref}
          className={cn(
            "flex",
            orientation === 'horizontal' ? "flex-1 relative" : 'flex-col w-full items-start', // flex-1 for horizontal items
            // Remove ::after styles and text color rules here, handle below
            className
          )}
          {...props}
        >
          {/* Container for Circle + Label */}
          <div className={cn(
              "flex z-10", // Ensure content is above the line
              orientation === 'horizontal' ? "flex-col text-center" : "flex-row items-center gap-3 w-full" // Adjust layout/spacing per orientation
          )}>
            {/* Animated Circle */}
            <motion.span
                className={cn(
                    "w-6 h-6 border-2 rounded-full flex justify-center items-center text-sm lg:w-10 lg:h-10",
                    orientation === 'horizontal' ? "mb-1 lg:mb-2" : "" // Add margin bottom only for horizontal
                )}
                variants={circleVariants}
                initial={false} // Don't animate on initial load, apply state directly
                animate={circleState}
                transition={transition}
            >
                {index + 1}
            </motion.span>

            {/* Label and Description */}
            <div className={cn("flex flex-col", orientation === 'horizontal' ? 'items-center' : 'items-start')}>
              <span className={cn(
                  "block whitespace-nowrap text-sm font-medium transition-colors duration-300", // Added transition for color
                  isActive ? 'text-primary' : 'text-muted-foreground', // Color based on active state
                  isCurrent && 'font-semibold' // Bold for current step label
              )}>
                  {label}
              </span>
              {description && (
                  <span className="text-xs text-muted-foreground">{description}</span>
              )}
            </div>
          </div>

          {/* Separator Lines (using motion.div) */}
          {!isLast && (
            // Wrapper div for positioning the lines
            <div className={cn(
                "absolute",
                orientation === 'horizontal'
                    ? "top-3 lg:top-5 left-4 w-full h-0.5" // Position horizontal line centered vertically with circle
                    : "top-0 left-5 w-0.5 h-full" // Position vertical line centered horizontally with circle
            )} aria-hidden="true">

                {/* Background Line */}
                <div className={cn(
                    "absolute bg-muted",
                    orientation === 'horizontal'
                        ? "top-0 h-full left-0 w-full" // Starts after circle+padding, goes right
                        : "left-0 w-full top-0 h-full" // Starts after circle+padding, goes down
                )}
                    style={{ '--circle-h': orientation === 'vertical' ? (index === 0 ? '2.5rem' : '2.5rem') : '0' } as React.CSSProperties} // Pass approximate height dynamically (lg:h-10 = 2.5rem) - Adjust if needed
                />
                 {/* Background Line (Left/Top part for Horizontal/Vertical) */}
                 { orientation === 'horizontal' && (
                     <div className="absolute top-0 h-full w-full bg-muted" />
                 )}


                {/* Animated Foreground Line */}
                <motion.div
                    className={cn(
                        "absolute bg-primary",
                        orientation === 'horizontal'
                            ? "top-0 h-full left-0 w-full origin-left" // Horizontal right part
                            : "left-0 w-full top-0 h-full origin-top" // Vertical bottom part
                    )}
                    style={{ '--circle-h': orientation === 'vertical' ? (index === 0 ? '2.5rem' : '2.5rem') : '0' } as React.CSSProperties}
                    variants={lineVariants}
                    initial={false}
                    animate={lineState}
                    transition={transition}
                />
            </div>
          )}
        </li>
      );
    }
);
StepperItem.displayName = 'StepperItem';

export { FormStepper, StepperItem };
export type { StepperProps, StepperItemProps };